import { NGUONG_LECH_PHAN_TRAM, REVENUE_STATUS_LABEL, type ReconciliationIssue } from '@qlk/shared';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { coQuyen, useToi } from '../../../lib/auth.js';
import {
  DataTable,
  EmptyState,
  MenuThaoTac,
  Money,
  Nut,
  PageHeader,
  StatusBadge,
  type CotBang,
} from '../../../ui/index.js';
import { tenKy } from '../../../lib/ky.js';
import { useDoiSoat, useXuLyDoiSoat } from '../api.js';

/** Màn hình đối soát: các dòng lệch quá ngưỡng, có thao tác xác nhận hoặc ghi chú. */
export function TrangDoiSoat() {
  const toi = useToi();
  const coSua = coQuyen(toi.data?.permissions, 'revenue', 'edit');

  const danhSach = useDoiSoat();
  const xuLy = useXuLyDoiSoat();

  const [dangGhiChu, datDangGhiChu] = useState<string | null>(null);
  const [ghiChu, datGhiChu] = useState('');

  function khoaDong(d: ReconciliationIssue): string {
    return `${d.channelId}-${d.period}`;
  }

  const cot: CotBang<ReconciliationIssue>[] = [
    {
      key: 'kenh',
      header: 'Kênh',
      render: (d) => (
        <div className="min-w-0">
          <Link
            to={`/kenh/${d.channelId}`}
            className="block truncate font-semibold text-chu hover:text-nhan"
          >
            {d.channelName}
          </Link>
          <p className="truncate text-phu text-chu-mo">{d.partnerName ?? 'Chưa gán đối tác'}</p>
        </div>
      ),
    },
    { key: 'ky', header: 'Kỳ', render: (d) => <span className="so-lieu">{tenKy(d.period)}</span> },
    {
      key: 'duKien',
      header: 'Net dự kiến',
      align: 'right',
      render: (d) => <Money value={d.expectedNet} />,
    },
    {
      key: 'saoKe',
      header: 'Net trên sao kê',
      align: 'right',
      render: (d) => <Money value={d.statementNet} />,
    },
    {
      key: 'lech',
      header: 'Lệch',
      align: 'right',
      render: (d) => (
        <span className="so-lieu font-semibold text-bad-chu">
          {Number(d.diffPercent) > 0 ? '+' : ''}
          {Number(d.diffPercent).toFixed(1).replace('.', ',')}%
        </span>
      ),
    },
    {
      key: 'trangThai',
      header: 'Trạng thái',
      render: (d) => (
        <div className="min-w-0">
          <StatusBadge tone={d.status === 'reconciled' ? 'ok' : 'warn'}>
            {REVENUE_STATUS_LABEL[d.status]}
          </StatusBadge>
          {d.note && <p className="mt-1 max-w-[260px] text-phu text-chu-mo">{d.note}</p>}
        </div>
      ),
    },
    {
      key: 'thaoTac',
      header: '',
      align: 'right',
      render: (d) =>
        !coSua ? (
          <span className="text-chu-mo">—</span>
        ) : dangGhiChu === khoaDong(d) ? (
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              xuLy.mutate(
                {
                  channelId: d.channelId,
                  period: d.period,
                  action: 'note',
                  note: ghiChu.trim() || null,
                },
                {
                  onSuccess: () => {
                    datDangGhiChu(null);
                    datGhiChu('');
                  },
                },
              );
            }}
          >
            <label htmlFor={`ghi-chu-${khoaDong(d)}`} className="sr-only">
              Ghi chú cho {d.channelName}
            </label>
            <input
              id={`ghi-chu-${khoaDong(d)}`}
              value={ghiChu}
              onChange={(e) => {
                datGhiChu(e.target.value);
              }}
              placeholder="Lý do lệch…"
              className="vung-bam w-[220px] rounded-[10px] border border-vien bg-be-mat px-3 text-co-ban text-chu"
            />
            <Nut kieu="chinh" type="submit" dangChay={xuLy.isPending}>
              Lưu
            </Nut>
            <Nut
              kieu="nhe"
              onClick={() => {
                datDangGhiChu(null);
              }}
            >
              Huỷ
            </Nut>
          </form>
        ) : (
          /*
            Một nút chính mỗi hàng ("Xác nhận"), "Ghi chú" vào ⋯ — luật của bộ
            thành phần mới. Hàng đã xác nhận thì không còn nút chính nào, chỉ
            còn ⋯ để sửa ghi chú.
          */
          <div className="flex items-center justify-end gap-1.5">
            {d.status !== 'reconciled' && (
              <Nut
                dangChay={xuLy.isPending}
                onClick={() => {
                  xuLy.mutate({ channelId: d.channelId, period: d.period, action: 'confirm' });
                }}
              >
                Xác nhận
              </Nut>
            )}
            <MenuThaoTac
              aria-label={`Thao tác với ${d.channelName} kỳ ${tenKy(d.period)}`}
              muc={[
                {
                  nhan: d.note ? 'Sửa ghi chú' : 'Thêm ghi chú',
                  onChon: () => {
                    datDangGhiChu(khoaDong(d));
                    datGhiChu(d.note ?? '');
                  },
                },
              ]}
            />
          </div>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        breadcrumb={['TÀI CHÍNH', 'Doanh thu']}
        title="Đối soát doanh thu"
        phu={`Lệch quá ${String(NGUONG_LECH_PHAN_TRAM)}% · ${String((danhSach.data ?? []).length)} dòng`}
        giaiThich={`Chỉ liệt kê kênh có số trên sao kê lệch quá ${String(NGUONG_LECH_PHAN_TRAM)}% so với số dự kiến. Xác nhận là chấp nhận số của sao kê; ghi chú để người sau biết vì sao lệch.`}
        actions={
          <Link to="/doanh-thu" className="text-co-ban font-semibold text-chu-phu hover:text-chu">
            ← Doanh thu
          </Link>
        }
      />

      {danhSach.error ? (
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState
            loi
            title="Không tải được danh sách đối soát"
            description={danhSach.error.message}
            action={
              <Nut
                onClick={() => {
                  void danhSach.refetch();
                }}
              >
                Thử lại
              </Nut>
            }
          />
        </div>
      ) : (
        <>
          {xuLy.error && (
            <p className="mb-4 rounded-[10px] bg-bad-nen px-4 py-3 text-co-ban text-bad-chu">
              {xuLy.error.message}
            </p>
          )}
          <DataTable
            caption="Các dòng doanh thu lệch so với sao kê"
            columns={cot}
            rows={danhSach.data ?? []}
            keyOf={khoaDong}
            dangTai={danhSach.isPending}
            khiRong={
              <EmptyState
                title="Không có dòng nào lệch"
                description="Mọi kỳ đã chốt đều khớp với sao kê của Network."
              />
            }
          />
        </>
      )}
    </>
  );
}
